import React, {useState} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Tab,
  Tabs,
  Tooltip,
} from "@material-ui/core";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import {AppliedContext} from "./applied_context_values";
import {contextSetPalette} from "./context_control";

interface AppliedContextButtonProps {
  appliedContext?: AppliedContext | null;
  isLoading?: boolean;
  containerStyles?: React.CSSProperties;
}

const sourceLabels = {
  // Every document uploaded since the record was added keeps its own frozen
  // copy. Older family documents have none, so they still read the family
  // setting, which a user can have edited since the analysis ran.
  document: "Recorded when this document was uploaded",
  family: "Current contract family setting",
};

const captionStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#757575",
  marginBottom: "6px",
};

const textStyle: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.9375rem",
  lineHeight: 1.5,
  maxHeight: "50vh",
  overflowY: "auto",
  padding: "8px 10px",
  borderRadius: "4px",
  border: "1px solid #e0e0e0",
  backgroundColor: "#fafafa",
};

const emptyTooltips = {
  client: "No client context was applied to this document",
  project: "No project context was applied to this document",
};

// A disabled tab drops pointer events, which also hides its tooltip, so the
// label keeps them for itself.
function renderTabLabel(label: string, isEnabled: boolean, tooltip: string) {
  if (isEnabled) {
    return label;
  }
  return (
    <Tooltip title={tooltip}>
      <span>{label}</span>
    </Tooltip>
  );
}

function buttonTooltip(isLoading?: boolean, isDisabled?: boolean): string {
  if (isLoading) {
    return "Loading the applied context";
  }
  if (isDisabled) {
    return "No context applied";
  }
  return "View the applied context";
}

const AppliedContextButton: React.FC<AppliedContextButtonProps> = ({
  appliedContext,
  isLoading,
  containerStyles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState(0);

  const client = appliedContext?.client;
  const project = appliedContext?.project;
  const hasClient = (client?.text ?? "").trim() !== "";
  const hasProject = (project?.text ?? "").trim() !== "";
  // While another file of the family loads, the context in hand belongs to the
  // file leaving the checklist, so show nothing rather than the wrong document.
  const isDisabled = isLoading || (!hasClient && !hasProject);

  const renderClientTab = () => {
    return (
      <>
        <div style={captionStyle}>
          {client?.source ? sourceLabels[client.source] : null}
          {client?.name ? ` · ${client.name}` : ""}
        </div>
        <div style={textStyle}>{client?.text}</div>
      </>
    );
  };

  const renderProjectTab = () => {
    return (
      <>
        {!project?.is_applied && (
          <div style={{...captionStyle, color: "#c62828"}}>
            Not applied. This contract family excludes project context.
          </div>
        )}
        <div
          style={{
            ...textStyle,
            ...(project?.is_applied ? {} : {color: "#9e9e9e"}),
          }}
        >
          {project?.text}
        </div>
      </>
    );
  };

  return (
    <div style={containerStyles}>
      <Tooltip title={buttonTooltip(isLoading, isDisabled)}>
        {/* A disabled button swallows pointer events, so the tooltip needs its
            own wrapper to stay reachable. */}
        <span style={{display: "inline-flex"}}>
          <Button
            size="small"
            variant="outlined"
            disabled={isDisabled}
            onClick={() => {
              setTab(hasClient ? 0 : 1);
              setIsOpen(true);
            }}
            startIcon={<LabelOutlinedIcon style={{fontSize: "0.875rem"}} />}
            style={{
              height: "28px",
              minWidth: 0,
              padding: "0 12px",
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.01em",
              ...(isDisabled
                ? {borderColor: "#e0e0e0", color: "#bdbdbd"}
                : contextSetPalette),
            }}
          >
            Context
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent style={{paddingTop: "8px"}}>
          <Tabs
            value={tab}
            onChange={(_, newTab) => setTab(newTab)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            style={{marginBottom: "16px", borderBottom: "1px solid #e0e0e0"}}
          >
            <Tab
              label={renderTabLabel("Client", hasClient, emptyTooltips.client)}
              disabled={!hasClient}
              style={{pointerEvents: "auto"}}
            />
            <Tab
              label={renderTabLabel(
                "Project",
                hasProject,
                emptyTooltips.project,
              )}
              disabled={!hasProject}
              style={{pointerEvents: "auto"}}
            />
          </Tabs>
          {tab === 0 ? renderClientTab() : renderProjectTab()}
        </DialogContent>
        <DialogActions style={{padding: "8px 24px 16px"}}>
          <Button size="small" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AppliedContextButton;
